import React, { useEffect, useMemo } from "react";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { Images } from "../../../utils/Images";
import { fetchCmsHomeAction } from "../../../store/slices/cmsSlice";

const CMS_PAGE = "How To Donate";
const CMS_SECTION = "How To Donate";
// Life section has its own video. The "How To Donate" CMS entry above stays the general video
// used by normal campaign pages (HowToDonate); HowToDonateV2 only renders in the Life section
// (DonationBoxV2, life / story pages). An optional CMS section "How To Donate - Life" overrides
// these defaults, so admins can still change the Life video later without a deploy.
const LIFE_CMS_SECTION = "How To Donate - Life";
const LIFE_VIDEO_LINK = "https://youtu.be/3tQUh1LyBXg";
const LIFE_VIDEO_THUMBNAIL = "https://i.ytimg.com/vi/3tQUh1LyBXg/maxresdefault.jpg";

const HowToDonateV2 = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const cmsHome = useSelector((state) => state.cms.home);

  useEffect(() => {
    if (!cmsHome) dispatch(fetchCmsHomeAction());
  }, [cmsHome, dispatch]);

  const { cms, lifeCms } = useMemo(() => {
    const data = Array.isArray(cmsHome?.data) ? cmsHome.data : [];
    return {
      cms: data.find((s) => s?.page === CMS_PAGE && s?.section === CMS_SECTION) || null,
      lifeCms: data.find((s) => s?.page === CMS_PAGE && s?.section === LIFE_CMS_SECTION) || null,
    };
  }, [cmsHome]);

  const sectionTitle = lifeCms?.section_title?.trim() || cms?.section_title?.trim() || "How To Donate";
  const thumbnailImage = lifeCms?.image || LIFE_VIDEO_THUMBNAIL;
  const videoLink = lifeCms?.link || LIFE_VIDEO_LINK;

  // Don't render if there's no thumbnail image from CMS
  if (!thumbnailImage && !videoLink) return null;

  const handleVideoClick = () => {
    if (videoLink) window.open(videoLink, "_blank");
  };

  return (
    <Box
      sx={{
        background: "#FFFFFF",
        border: "1px solid #F1F1F1",
        borderRadius: "10px",
        p: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* ── Green header bar ── */}
      <Box
        sx={{
          background: "#F5F8ED",
          borderRadius: "5px",
          px: "16px",
          py: "8px",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Red Hat Display",
            fontWeight: 700,
            fontSize: "14px",
            lineHeight: "21px",
            color: "#8EA946",
          }}
        >
          {sectionTitle.endsWith(":") ? sectionTitle : `${sectionTitle}:`}
        </Typography>
      </Box>

      {/* ── Video thumbnail ── */}
      {thumbnailImage && (
        <Box
          onClick={handleVideoClick}
          sx={{
            position: "relative",
            borderRadius: "10px",
            overflow: "hidden",
            height: isMobile ? "160px" : "155px",
            cursor: videoLink ? "pointer" : "default",
            transition: "transform 0.2s ease",
            "&:hover": videoLink ? { transform: "scale(1.01)" } : {},
          }}
        >
          {/* Thumbnail image */}
          <Box
            component="img"
            src={thumbnailImage}
            alt="How to donate"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />

          {/* Dark overlay */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: "rgba(20,20,20,0.5)",
              borderRadius: "10px",
            }}
          />

          {/* YouTube play icon centered */}
          {videoLink && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "44px",
                height: "44px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                component="img"
                src={Images.youtube}
                alt="Play"
                sx={{ width: "32px", height: "32px" }}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default HowToDonateV2;
